import {
  ElementFieldMappingsValidationResult,
  ElementFieldMappingValidationError,
  ElementFieldMappingValidationErrorCode,
  ElementFieldMappingWithKey,
  ERROR_CODES,
  KintoneExportSetting,
  KintoneFormField,
  KintoneFormFields,
  MetadataFieldMapping,
  MetadataFieldMappingValidationError,
  MetadataFieldMappingValidationResult,
  nullToUndefinedDeep,
  validateElementFieldMappings as validateElementFieldMappingsCore,
  validateMetadataFieldMapping as validateMetadataFieldMappingCore,
} from "@graffer-inc/cloud-gov-core";
import { IFormSchemaV2 } from "@graffer-inc/form-schema";
import {
  CreateKintoneExportSettingInput,
  UpdateKintoneExportSettingInput,
} from "@graffer-inc/smart-apply";

// サーバーへ投げられる状態であれば true を、
// 不正な状態であれば false を返す。
export const validateCreateSettingInput = ({
  name,
  description,
  formResourceId,
  kintoneAppId,
  kintoneSubdomain,
  kintoneAPIToken,
  disabled,
}: CreateKintoneExportSettingInput): boolean => {
  // GUI からは無効な設定として作成することを強制する
  if (!disabled) {
    return false;
  }

  if (validateName(name)) {
    return false;
  }

  if (validateDescription(description)) {
    return false;
  }

  if (!formResourceId) {
    return false;
  }

  if (!kintoneAppId) {
    return false;
  }

  if (!kintoneSubdomain) {
    return false;
  }

  if (!kintoneAPIToken) {
    return false;
  }

  return true;
};

// サーバーへ投げられる状態であれば true を、
// 不正な状態であれば false を返す。
export const validateUpdateSettingInput = (
  {
    name,
    description,
    disabled,
    elementFieldMappings,
    metadataFieldMapping,
  }: UpdateKintoneExportSettingInput,
  formSchema: IFormSchemaV2,
  kintoneFields?: KintoneFormFields,
): boolean => {
  if (validateName(name)) {
    return false;
  }

  if (validateDescription(description)) {
    return false;
  }

  // 無効な設定ならば、他の項目は検査しない。
  if (disabled) {
    return true;
  }

  const { errors, usedKintoneFieldCode } = validateMetadataFieldMapping({
    value: nullToUndefinedDeep(metadataFieldMapping),
    kintoneFields,
  });
  if (errors.length > 0) {
    return false;
  }

  const { errors: elemErrors } = validateElementFieldMappings({
    value: elementFieldMappings.map((mapping, i) => {
      return {
        ...mapping,
        mappingKey: i,
      };
    }),
    formSchema,
    kintoneFields,
    usedKintoneFieldCode,
  });
  if (elemErrors.length > 0) {
    return false;
  }

  return true;
};

export const validateName = (
  value: KintoneExportSetting["name"],
): string | undefined => {
  if (!value) {
    return "設定名称は必ず入力してください。";
  }
  if (value.length > 256) {
    return "設定名称は256文字以内で入力してください。";
  }
  return undefined;
};

export const validateDescription = (
  value: KintoneExportSetting["description"],
): string | undefined => {
  if (!!value && value.length > 256) {
    return "説明文は256文字以内で入力してください。";
  }
  return undefined;
};

// https://jp.cybozu.help/general/ja/admin/list_service/domain.html#list_service_domain_30
// 正規表現で https://{アルファベット、数字、ハイフンによる任意の組み合わせ。3文字以上32文字以下}.cybozu.com/k/{1桁以上の数字}/ へ適合しているか検査する
export const kintoneAppUrlRegex =
  /^https:\/\/[a-zA-Z0-9-]{3,32}\.cybozu.com\/k\/[0-9]+\/$/;

export const validateMetadataFieldMapping = ({
  value,
  kintoneFields,
  usedKintoneFieldCode,
}: {
  value: MetadataFieldMapping;
  kintoneFields?: KintoneFormFields;
  usedKintoneFieldCode?: Set<KintoneFormField["code"]>;
}): MetadataFieldMappingValidationResult => {
  if (kintoneFields) {
    return validateMetadataFieldMappingCore({
      mapping: value,
      kintoneFields,
      usedKintoneFieldCode: new Set(usedKintoneFieldCode),
    });
  }

  // kintone field が提供されない場合は、最低限、空入力の検査のみ行う
  const errors: MetadataFieldMappingValidationError[] = [];
  Object.keys(value).forEach((key) => {
    const typedKey = key as keyof MetadataFieldMapping;
    const v = value[typedKey];
    if (!v) {
      errors.push({
        mappingKey: typedKey,
        errorCode: "KINTONE_FIELD_CODE_EMPTY",
      });
      return;
    }
  });
  return {
    errors,
    usedKintoneFieldCode: new Set(),
  };
};

export const fieldMappingErrorCodeToMessage = (
  errorCode: ElementFieldMappingValidationErrorCode,
): string => {
  switch (errorCode) {
    case ERROR_CODES.ELEMENT_ID_EMPTY:
      return "「フォームの入力項目」を設定してください。";
    case ERROR_CODES.KINTONE_FIELD_CODE_EMPTY:
      return "「kintone フィールド」を設定してください。";
    case ERROR_CODES.ELEMENT_NOT_FOUND:
      return "フォームに存在する項目から設定してください。"; // GUI では発生しないはず
    case ERROR_CODES.KINTONE_FIELD_NOT_FOUND:
      return "kintone フィールドに存在する項目から設定してください。"; // GUI では発生しないはず
    case ERROR_CODES.REPEATABLE_ELEMENT_DETECTED:
      return "繰り返し要素は直接指定できません。内部の要素同士でマッピングを設定してください。"; // GUI では発生しないはず
    case ERROR_CODES.FUNCTION_ELEMENT_DETECTED:
      return "関数要素は指定できません。"; // GUI では発生しないはず
    case ERROR_CODES.STATIC_ELEMENT_DETECTED:
      return "静的要素は指定できません。"; // GUI では発生しないはず
    case ERROR_CODES.TYPE_MISMATCH:
      return "「フォームの入力項目」のタイプと、「kintone フィールド」のタイプとが噛み合っていません。正しい組み合わせで設定してください。";
    case ERROR_CODES.KINTONE_FIELD_ALREADY_USED:
      return "指定された「kintone フィールド」がすでに別の箇所で利用されています。マッピング先がユニークになるように設定してください。";
    case ERROR_CODES.KINTONE_SUBTABLE_ALREADY_USED:
      return "指定された「kintone フィールド」が所属するテーブルがすでに別の箇所で利用されています。特定の繰り返し項目の要素からのみマッピングされるよう設定してください。";
    case ERROR_CODES.LAYER_MISMATCH:
      return "「フォームの入力項目」と「kintone フィールド」の階層が噛み合っていません。繰り返し項目やテーブルの子要素が、階層をまたがないように設定してください。";
    case ERROR_CODES.OPTION_MISMATCH:
      return "マッピング対象の選択肢が噛み合っていません。お互いの選択肢に過不足がないように修正してください。";
    case ERROR_CODES.FORM_SCHEMA_FILE_INPUT_ALREADY_USED:
      return "指定されたファイル入力項目がすでに別の箇所で利用されています。ファイル入力項目のマッピング先は単一になるように設定してください。";
  }
};

export const validateElementFieldMappings = ({
  value,
  formSchema,
  kintoneFields,
  usedKintoneFieldCode,
}: {
  value: ElementFieldMappingWithKey[];
  formSchema: IFormSchemaV2;
  kintoneFields?: KintoneFormFields;
  usedKintoneFieldCode?: Set<KintoneFormField["code"]>;
}): ElementFieldMappingsValidationResult => {
  if (kintoneFields) {
    return validateElementFieldMappingsCore({
      mappings: value,
      formSchema,
      kintoneFields,
      usedKintoneFieldCode: new Set(usedKintoneFieldCode),
    });
  }

  // kintone field が提供されない場合は、最低限、空入力の検査のみ行う
  const errors: ElementFieldMappingValidationError[] = [];
  value.forEach((mapping) => {
    if (!mapping.formElementId) {
      errors.push({
        errorCode: "ELEMENT_ID_EMPTY",
        mappingKey: mapping.mappingKey,
      });
    }
    if (!mapping.kintoneFieldCode) {
      errors.push({
        errorCode: "KINTONE_FIELD_CODE_EMPTY",
        mappingKey: mapping.mappingKey,
      });
    }
  });
  return {
    errors,
    usedKintoneFieldCode: new Set(),
  };
};
